// Single source of truth for account-integrity / verification / MFA client logic.
// No privileged decision is made here — every answer comes from an edge function
// that re-validates the caller server-side.
import { supabase } from '@/lib/supabase';

export type Classification =
  | 'unreviewed'
  | 'internal_test'
  | 'legitimate'
  | 'duplicate'
  | 'requires_identity_verification'
  | 'suspicious_automation'
  | 'fraud_suspected'
  | 'unable_to_determine';

export const CLASSIFICATIONS: { value: Classification; label: string; tone: string }[] = [
  { value: 'unreviewed', label: 'Unreviewed', tone: 'bg-slate-100 text-slate-700' },
  { value: 'internal_test', label: 'Confirmed internal test account', tone: 'bg-sky-100 text-sky-800' },
  { value: 'legitimate', label: 'Legitimate user', tone: 'bg-emerald-100 text-emerald-800' },
  { value: 'duplicate', label: 'Duplicate account', tone: 'bg-amber-100 text-amber-800' },
  { value: 'requires_identity_verification', label: 'Requires identity verification', tone: 'bg-amber-100 text-amber-800' },
  { value: 'suspicious_automation', label: 'Suspicious automation', tone: 'bg-orange-100 text-orange-800' },
  { value: 'fraud_suspected', label: 'Fraud suspected', tone: 'bg-rose-100 text-rose-800' },
  { value: 'unable_to_determine', label: 'Unable to determine', tone: 'bg-slate-200 text-slate-700' }
];

export const COHORTS: { value: string; label: string }[] = [
  { value: '', label: 'All flagged accounts' },
  { value: 'ip_cluster', label: 'Shared signup IP cluster' },
  { value: 'duplicate_phone', label: 'Duplicate phone' },
  { value: 'listing_burst', label: 'Listing burst' },
  { value: 'never_signed_in', label: 'Never signed in' },
  { value: 'admin', label: 'Administrators' }
];

export interface ReviewRow {
  user_id: string;
  email: string | null;
  user_type: string | null;
  cohorts: string[];
  account_created_at: string | null;
  last_sign_in_at: string | null;
  email_verification_state: string | null;
  phone_verified: boolean;
  listing_count: number;
  credits: number;
  payment_count: number;
  payment_total: number;
  shared_ip_hash: string | null;
  shared_ip_count: number;
  masked_phone: string | null;
  phone_hash: string | null;
  indicators: string[];
  risk_level: string;
  classification: Classification;
  reviewer_notes: string | null;
  reviewer_email: string | null;
  classified_at: string | null;
}

/** Invoke an edge function with the caller's live session token. */
async function call<T = any>(fn: string, body: Record<string, unknown>): Promise<{ data: T | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    const msg = (data as any)?.error || error.message || 'request_failed';
    return { data: (data as T) ?? null, error: String(msg) };
  }
  if ((data as any)?.error) return { data: data as T, error: String((data as any).error) };
  return { data: data as T, error: null };
}

export const reviewQueue = {
  list: (cohort = '') => call('admin-account-review', { action: 'list', cohort }),
  rebuild: () => call('admin-account-review', { action: 'rebuild' }),
  classify: (user_id: string, classification: Classification, notes: string) =>
    call('admin-account-review', { action: 'classify', user_id, classification, notes })
};

export const mfa = {
  status: () => call('admin-mfa', { action: 'status' }),
  overview: () => call('admin-mfa', { action: 'admin_overview' }),
  confirmEnrollment: () => call<{ recovery_codes: string[] }>('admin-mfa', { action: 'confirm_enrollment' }),
  consumeRecovery: (code: string) => call('admin-mfa', { action: 'consume_recovery', code }),
  setEnforcement: (enabled: boolean, force = false) => call('admin-mfa', { action: 'set_enforcement', enabled, force }),
  revokeMySessions: (reason: string) => call('admin-mfa', { action: 'revoke_my_sessions', reason }),
  revokeSessions: (user_id: string, reason: string) => call('admin-mfa', { action: 'revoke_sessions', user_id, reason }),
  resetMfa: (user_id: string, reason: string) => call('admin-mfa', { action: 'reset_mfa', user_id, reason })
};

export const verification = {
  status: () => call('account-verification', { action: 'status' }),
  syncEmail: () => call('account-verification', { action: 'sync_email' }),
  resendEmail: () => call('account-verification', { action: 'resend_email' }),
  phoneStart: (phone: string) => call('account-verification', { action: 'phone_start', phone }),
  phoneCheck: (phone: string, code: string) => call('account-verification', { action: 'phone_check', phone, code })
};

/** True when the current access token proves a second factor was used. */
export async function currentAal(): Promise<string> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return 'none';
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return String(payload.aal || 'aal1');
  } catch {
    return 'aal1';
  }
}

export const fmtDate = (v?: string | null) =>
  v ? new Date(v).toISOString().replace('T', ' ').slice(0, 16) + ' UTC' : '—';

export const money = (cents: number) => `$${((cents || 0) / 100).toFixed(2)}`;
